import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  ShieldCheck,
  Users,
  Truck,
  Shirt,
  Cpu,
  Cog,
  Building2,
  HardHat,
  Compass,
  Layers,
  Network,
  TrendingUp,
  CheckCircle2,
  AlertTriangle,
  Target,
  Lightbulb,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";

const SECTORS = [
  { icon: Users, label: "Personaldienstleister", text: "Fachkräftegewinnung für sicherheitsrelevante Projekte." },
  { icon: Truck, label: "Logistik", text: "Komplexe Transport- und Versorgungsaufgaben." },
  { icon: Shirt, label: "Textil", text: "Spezialbekleidung und Schutzausrüstung." },
  { icon: Cpu, label: "IT & Software", text: "Plattformen, Cybersicherheit, digitale Lösungen." },
  { icon: Cog, label: "Maschinenbau", text: "Komponenten, Systeme und Spezialfertigung." },
  { icon: Lightbulb, label: "Elektronik", text: "Embedded Systems, Sensorik, Steuerungstechnik." },
  { icon: Building2, label: "Bauwirtschaft", text: "Infrastruktur und sicherheitsrelevante Liegenschaften." },
  { icon: HardHat, label: "Ingenieurbüros", text: "Planung, Engineering und Beratung entlang der Wertschöpfungskette." },
];

const DRIVERS = [
  {
    icon: TrendingUp,
    title: "Langfristige Investitionsprogramme",
    text: "Bund, NATO-Mitgliedstaaten und die EU erhöhen ihre Ausgaben für Verteidigung, Sicherheit und Resilienz spürbar – über viele Jahre hinweg.",
  },
  {
    icon: Network,
    title: "Breite Wertschöpfungsketten",
    text: "Bedarfe entstehen weit über die Produktion militärischer Systeme hinaus: Infrastruktur, Digitalisierung, Cybersicherheit, Energie, kritische Lieferketten.",
  },
  {
    icon: ShieldCheck,
    title: "Resilienz als Standortfaktor",
    text: "Verlässliche Lieferfähigkeit, robuste Prozesse und transparente Strukturen werden zu strategischen Wettbewerbsvorteilen.",
  },
  {
    icon: Compass,
    title: "Frühzeitige Positionierung",
    text: "Wer früh prüft, welche eigenen Kompetenzen relevant sind, sichert sich strategische Vorteile gegenüber abwartenden Marktteilnehmern.",
  },
];

const REQUIREMENTS = [
  "Belastbare Prozesse und nachvollziehbare Qualitätsstandards",
  "Stabile, transparente und dokumentierte Lieferketten",
  "Digitale Nachvollziehbarkeit entlang der Wertschöpfung",
  "Hohe organisatorische und betriebliche Professionalität",
  "Sicherheits- und Compliance-konforme Strukturen",
  "Belege zu Lieferfähigkeit, Skalierbarkeit und Krisenfestigkeit",
];

const X10_SUPPORT = [
  {
    icon: Target,
    title: "Strategische Standortbestimmung",
    text: "Wir analysieren bestehende Strukturen, Kompetenzen und Produkte – und identifizieren Anknüpfungspunkte zu sicherheitsrelevanten Wertschöpfungsketten.",
  },
  {
    icon: Layers,
    title: "Prozesse & Organisation",
    text: "Optimierung von Abläufen, Aufbau transparenter Strukturen und Digitalisierung relevanter Prozesse für die Anforderungen dieses Marktes.",
  },
  {
    icon: ShieldCheck,
    title: "Anforderungen verstehen",
    text: "Wir vermitteln das notwendige Wissen zu regulatorischen, sicherheitsrelevanten und marktspezifischen Anforderungen.",
  },
  {
    icon: Network,
    title: "Netzwerk & Positionierung",
    text: "Auf Wunsch begleiten wir Sie bei der Marktpositionierung, beim Aufbau relevanter Netzwerke und ersten konkreten Geschäftsmöglichkeiten.",
  },
];

export default function DefenseSecurity() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="defense-security-page" className="font-body bg-white text-slate-900 min-h-screen">
      <SEO
        title="Chancen im Defense & Security Markt | X10 Management"
        description="Die Verteidigungs- und Sicherheitswirtschaft ist ein unterschätzter Wachstumsmarkt für den Mittelstand. X10 begleitet Unternehmen bei der strategischen Positionierung in sicherheitsrelevanten Wertschöpfungsketten."
      />
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0A192F] text-white pt-32 lg:pt-36">
        <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-[#C9A96E]/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-[560px] h-[560px] rounded-full bg-[#1E3A8A]/40 blur-3xl pointer-events-none" />
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Link
              to="/#services"
              data-testid="ds-back"
              className="inline-flex w-fit items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors mb-8"
            >
              <ArrowLeft size={16} /> Zurück zu Leistungen
            </Link>
            <div>
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-extrabold text-[#C9A96E]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#C9A96E]" />
                Discipline 02 · Defense &amp; Security
              </span>
            </div>
            <h1 className="mt-5 font-heading font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-4xl">
              Die Verteidigungs- und Sicherheitswirtschaft:
              <span className="block text-[#C9A96E] mt-2">Ein unterschätzter Wachstumsmarkt für den Mittelstand.</span>
            </h1>
            <p className="mt-6 text-lg lg:text-xl text-white/85 font-light leading-relaxed max-w-3xl">
              Bund, NATO und EU erhöhen ihre Investitionen in Verteidigung, Sicherheit und Resilienz langfristig.
              Mittelständische Unternehmen vieler Branchen können davon profitieren – wenn sie sich frühzeitig
              strategisch positionieren.
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/#contact"
                data-testid="ds-cta-contact"
                className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-[#0A192F] font-semibold hover:bg-[#C9A96E] hover:text-white transition-all duration-300"
              >
                Beratungsgespräch vereinbaren
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href="#x10-support"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/30 font-semibold hover:bg-white/10 transition-all duration-300"
              >
                Wie X10 begleitet
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden ring-1 ring-white/10 max-w-md ml-auto">
              <img
                src="https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?auto=format&fit=crop&w=1200&q=80"
                alt="Industrielle Fertigung in der Verteidigungs- und Sicherheitswirtschaft"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0A192F]/80 via-[#0A192F]/30 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A96E] font-semibold">Strategic Field</div>
                <div className="font-heading text-lg font-semibold mt-1 leading-tight">
                  Defense, Security &amp; Resilience
                </div>
                <div className="text-sm text-white/80 mt-1">Wachstumsmarkt für den Mittelstand</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-sm uppercase tracking-[0.25em] font-extrabold text-[#1E3A8A]">Marktverständnis</span>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold text-[#0A192F] tracking-tight leading-tight">
            Mehr als Panzer und Flugzeuge.
          </h2>
          <div className="mt-8 space-y-6 text-lg text-slate-700 leading-relaxed">
            <p>
              Wenn von der Verteidigungsindustrie gesprochen wird, denken viele zunächst an Panzer, Flugzeuge oder große
              Rüstungskonzerne. Tatsächlich greift diese Sichtweise deutlich zu kurz. Die moderne Verteidigungs- und
              Sicherheitswirtschaft umfasst heute ein weit verzweigtes Ökosystem aus Unternehmen unterschiedlichster
              Branchen – und bietet damit auch für viele Mittelständler neue Geschäftschancen.
            </p>
            <p>
              Die sicherheitspolitischen Entwicklungen der vergangenen Jahre haben dazu geführt, dass Bund,
              NATO-Mitgliedstaaten und die Europäische Union ihre Investitionen in Verteidigung, Sicherheit und
              Resilienz deutlich erhöhen. Die dafür bereitgestellten Mittel fließen jedoch nicht ausschließlich an
              klassische Rüstungsunternehmen. Vielmehr entstehen entlang der gesamten Wertschöpfungskette Bedarfe, die
              weit über die eigentliche Produktion militärischer Systeme hinausgehen.
            </p>
          </div>
        </div>
      </section>

      {/* Sectors Grid */}
      <section className="bg-[#F3F4F6] py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-sm uppercase tracking-[0.25em] font-extrabold text-[#1E3A8A]">
              Wer profitiert
            </span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold text-[#0A192F] tracking-tight leading-tight">
              Branchen mit unmittelbaren Chancen.
            </h2>
            <p className="mt-5 text-lg text-slate-700 leading-relaxed">
              Bedarfe entstehen entlang der gesamten Wertschöpfungskette – weit über klassische Rüstungsunternehmen
              hinaus. Diese Branchen können bereits heute mit bestehenden Kompetenzen profitieren:
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SECTORS.map(({ icon: Icon, label, text }, i) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="group p-6 rounded-2xl bg-white border border-slate-200 hover:border-[#C9A96E]/60 hover:shadow-[0_0_24px_rgba(201,169,110,0.15)] transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-full bg-[#0A192F]/5 border border-[#C9A96E]/40 flex items-center justify-center">
                  <Icon size={18} className="text-[#0A192F]" />
                </div>
                <div className="mt-4 font-heading text-base font-semibold text-[#0A192F]">{label}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why now / Drivers */}
      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="text-sm uppercase tracking-[0.25em] font-extrabold text-[#1E3A8A]">Warum jetzt</span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold text-[#0A192F] tracking-tight leading-tight">
              Vier Treiber, die den Markt definieren.
            </h2>
            <p className="mt-6 text-lg text-slate-700 leading-relaxed">
              Viele Faktoren sprechen dafür, dass die Verteidigungs- und Sicherheitswirtschaft in den kommenden Jahren
              zu einem der bedeutendsten Konjunktur- und Investitionsmotoren Europas wird.
            </p>
            <div className="mt-8 p-6 rounded-2xl bg-[#0A192F] text-white">
              <div className="text-xs uppercase tracking-[0.25em] font-extrabold text-[#C9A96E]">Insight</div>
              <p className="mt-3 text-slate-200 leading-relaxed">
                Die Investitionen betreffen nicht nur militärische Fähigkeiten, sondern auch{" "}
                <span className="text-white font-medium">Infrastruktur, Digitalisierung, Cybersicherheit, Energie,
                kritische Lieferketten und industrielle Kapazitäten</span>.
              </p>
            </div>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {DRIVERS.map(({ icon: Icon, title, text }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="p-6 rounded-2xl bg-[#F3F4F6] border border-slate-200 hover:border-[#1E3A8A]/40 transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-full bg-[#1E3A8A]/10 border border-[#1E3A8A]/30 flex items-center justify-center">
                  <Icon size={18} className="text-[#1E3A8A]" />
                </div>
                <div className="mt-4 font-heading text-lg font-semibold text-[#0A192F]">{title}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="bg-[#0A192F] text-white py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[440px] h-[440px] rounded-full bg-[#C9A96E]/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-extrabold text-[#C9A96E]">
              <AlertTriangle size={14} /> Anforderungen
            </span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
              Höhere Standards. Härtere Auswahl. <span className="text-[#C9A96E]">Klare Chancen.</span>
            </h2>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed">
              Auftraggeber erwarten belastbare Prozesse, stabile Lieferketten, digitale Nachvollziehbarkeit und eine
              hohe organisatorische Professionalität. Für viele Unternehmen bedeutet dies, bestehende Strukturen
              weiterzuentwickeln und sich auf neue Marktanforderungen einzustellen.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid sm:grid-cols-2 gap-3">
              {REQUIREMENTS.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 p-4 rounded-xl bg-white/5 border border-white/10"
                >
                  <CheckCircle2 size={18} className="text-[#C9A96E] mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-200 leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* X10 Support */}
      <section id="x10-support" className="bg-white py-16 lg:py-24 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-sm uppercase tracking-[0.25em] font-extrabold text-[#1E3A8A]">Unser Beitrag</span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold text-[#0A192F] tracking-tight leading-tight">
              Wie X10 Unternehmen auf diesem Weg begleitet.
            </h2>
            <p className="mt-6 text-lg text-slate-700 leading-relaxed">
              Wir analysieren bestehende Strukturen, identifizieren Potenziale und vermitteln das notwendige Wissen, um
              die Anforderungen dieses Marktes zu verstehen und erfolgreich zu erfüllen. Unser Ziel: Unternehmen
              nachhaltig zu stärken und sie zu befähigen, neue Marktchancen eigenständig zu nutzen.
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 gap-4">
            {X10_SUPPORT.map(({ icon: Icon, title, text }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="p-6 rounded-2xl bg-[#0A192F] text-white border border-[#0A192F] hover:border-[#3B82F6]/50 hover:shadow-[0_0_30px_rgba(59,130,246,0.2)] transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-full bg-white/10 border border-[#C9A96E]/30 flex items-center justify-center">
                  <Icon size={18} className="text-[#C9A96E]" />
                </div>
                <div className="mt-4 font-heading text-lg font-semibold">{title}</div>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed">{text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing Statement */}
      <section className="bg-[#F3F4F6] py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative p-8 lg:p-12 rounded-3xl bg-white border border-slate-200 shadow-sm">
            <div className="absolute -top-4 left-8 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#0A192F] text-[10px] uppercase tracking-[0.3em] font-extrabold text-[#C9A96E]">
              Bottom Line
            </div>
            <h3 className="mt-2 font-heading text-2xl sm:text-3xl font-bold text-[#0A192F] tracking-tight leading-tight">
              Kein Nischenmarkt mehr. <span className="text-[#1E3A8A]">Ein strategisches Wachstumsfeld.</span>
            </h3>
            <div className="mt-6 space-y-4 text-slate-700 leading-relaxed">
              <p>
                Die Verteidigungs- und Sicherheitswirtschaft entwickelt sich zunehmend zu einem bedeutenden
                Wirtschaftsfaktor mit Auswirkungen auf zahlreiche Branchen. Unternehmen, die flexibel reagieren und die
                entstehenden Chancen frühzeitig erkennen, erschließen zusätzliche Wachstumspotenziale und stärken ihre
                Marktposition nachhaltig.
              </p>
              <p className="text-[#0A192F] font-medium">
                Für viele Mittelständler stellt sich daher nicht mehr die Frage, ob die Verteidigungs- und
                Sicherheitswirtschaft relevant wird – sondern wie sie von dieser Entwicklung profitieren können.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A192F] text-white py-16 lg:py-20 relative overflow-hidden">
        <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#1E3A8A]/40 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#C9A96E]/15 blur-3xl pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-extrabold text-[#C9A96E]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C9A96E]" /> Get in touch
          </span>
          <h3 className="mt-5 font-heading text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
            Bereit, Ihr Unternehmen strategisch zu positionieren?
          </h3>
          <p className="mt-5 text-lg text-slate-300 leading-relaxed">
            Wir prüfen mit Ihnen, welche Ihrer bestehenden Stärken bereits heute in sicherheitsrelevanten
            Wertschöpfungsketten gefragt sind – und entwickeln gemeinsam einen klaren Fahrplan.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link
              to="/#contact"
              data-testid="ds-cta-bottom"
              className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-[#0A192F] font-semibold hover:bg-[#C9A96E] hover:text-white transition-all duration-300"
            >
              Beratungsgespräch vereinbaren
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/#services"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/30 font-semibold hover:bg-white/10 transition-all duration-300"
            >
              Weitere Leistungen entdecken
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
